import type { ContactFormData, ContactFormSubmitCallbacks } from './contact-form.types'

/** Джерело заявки (origin без протоколу) для SSR-safe використання. */
export function getRequestSource(): string | undefined {
  if (typeof window === 'undefined') return undefined
  return window.location.origin.replace(/^https?:\/\//, '')
}

export interface UtmParams {
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
  utmContent?: string
  utmTerm?: string
  referrer?: string
  rawUrl?: string
}

const UTM_SESSION_KEY = '__landing_utm'

/** Збирає UTM-параметри. Читає з sessionStorage (збережено плагіном utm-capture при першому завантаженні),
 *  щоб не губити дані при hash-навігації всередині SPA. */
export function getUtmParams(): UtmParams {
  if (typeof window === 'undefined') return {}
  try {
    const stored = sessionStorage.getItem(UTM_SESSION_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      return {
        utmSource:   parsed.utm_source   ?? undefined,
        utmMedium:   parsed.utm_medium   ?? undefined,
        utmCampaign: parsed.utm_campaign ?? undefined,
        utmContent:  parsed.utm_content  ?? undefined,
        utmTerm:     parsed.utm_term     ?? undefined,
        referrer:    parsed.referrer     ?? undefined,
        rawUrl:      parsed.raw_url      ?? undefined,
      }
    }
  } catch { /* sessionStorage unavailable */ }
  // Fallback: поточний URL (якщо плагін не встиг або sessionStorage заблокований)
  const p = new URLSearchParams(window.location.search)
  return {
    utmSource:   p.get('utm_source')   ?? undefined,
    utmMedium:   p.get('utm_medium')   ?? undefined,
    utmCampaign: p.get('utm_campaign') ?? undefined,
    utmContent:  p.get('utm_content')  ?? undefined,
    utmTerm:     p.get('utm_term')     ?? undefined,
    referrer:    document.referrer     || undefined,
    rawUrl:      window.location.href,
  }
}

function trimOptional(value: unknown): string | undefined {
  if (value == null || String(value).trim() === '') return undefined
  return String(value).trim()
}

export interface BuildPayloadOptions {
  showRegion: boolean
  courseId?: string
}

export interface BuildEmailPayloadOptions extends BuildPayloadOptions {
  countryIso?: string
}

export function buildEmailFormData(
  raw: Record<string, unknown>,
  options: BuildEmailPayloadOptions
): ContactFormData {
  const { showRegion, courseId, countryIso } = options
  return {
    fullName: String(raw.fullName ?? '').trim(),
    phone: '',
    email: String(raw.email ?? '').trim(),
    locality: trimOptional(raw.locality),
    region: showRegion ? trimOptional(raw.region) : undefined,
    countryIso: countryIso ?? undefined,
    message: trimOptional(raw.message),
    privacyAccepted: raw.privacyAccepted === true,
    courseId: courseId?.trim() || undefined,
  }
}

export function buildPhoneFormData(
  raw: Record<string, unknown>,
  options: BuildPayloadOptions
): ContactFormData {
  const { showRegion, courseId } = options
  return {
    fullName: String(raw.fullName ?? '').trim(),
    phone: String(raw.phone ?? '').replace(/\D/g, '').trim(),
    email: '',
    locality: trimOptional(raw.locality),
    region: showRegion ? trimOptional(raw.region) : undefined,
    message: trimOptional(raw.message),
    privacyAccepted: raw.privacyAccepted === true,
    courseId: courseId?.trim() || undefined,
  }
}

export interface RunSubmitFlowContext {
  setLoading: (v: boolean) => void
  setError: (v: string | null) => void
  resetForm: () => void
}

/** Єдиний потік: валідація → збір даних → submit → reset/success або error. */
export async function runContactSubmitFlow(
  validate: () => Promise<{ valid: boolean }>,
  getValues: () => Record<string, unknown> | undefined,
  buildData: (values: Record<string, unknown>) => ContactFormData,
  submitToApi: (data: ContactFormData) => Promise<void>,
  ctx: RunSubmitFlowContext,
  callbacks?: ContactFormSubmitCallbacks
): Promise<void> {
  const result = await validate()
  const values = getValues()
  if (!result.valid || !values) return

  const data = buildData(values)
  ctx.setError(null)
  ctx.setLoading(true)
  try {
    await submitToApi(data)
    ctx.resetForm()
    callbacks?.onSuccess?.(data)
  } catch (err) {
    const error = err instanceof Error ? err : new Error(String(err))
    const statusCode = (err as { statusCode?: number }).statusCode
    const message = callbacks?.getErrorMessage
      ? callbacks.getErrorMessage(error, statusCode)
      : error.message
    ctx.setError(message)
    callbacks?.onError?.(error)
    throw error
  } finally {
    ctx.setLoading(false)
  }
}
